import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BrowserProvider,
  Contract,
  JsonRpcProvider,
  formatUnits,
  parseUnits,
} from "ethers";

import {
  useAppKitAccount,
  useAppKitProvider,
  useAppKitNetwork,
} from "@reown/appkit/react";


/* =========================================================
   CONFIG
========================================================= */

const BSC_CHAIN_ID = 56;

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

const CONTRACT_ADDRESS =
  process.env.REACT_APP_KIND_RUSH_CONTRACT_ADDRESS ||
  "0x900ADA53B342ebdF097465cefDD65Ac13Cb928f6";

/*
 * This is only needed by the frontend when approving KIND
 * before fundRewardContract().
 *
 * createCampaign() DOES NOT receive the KIND address.
 */
const KIND_TOKEN_ADDRESS =
  process.env.REACT_APP_KIND_TOKEN_ADDRESS ||
  "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";


/* =========================================================
   CONTRACT ABI
========================================================= */

const ABI = [
  /* READ */

  "function owner() view returns (address)",

  "function paused() view returns (bool)",

  "function getActiveCampaignId() view returns (uint256)",

  `function getGlobalView(
      uint256 campaignId
    )
    view
    returns (
      (
        uint256 campaignId,
        uint256 totalParticipants,
        uint256 totalQualified,
        uint256 totalCompleted,
        uint256 totalDisqualified,
        uint256 totalVerifiedTrades,
        uint256 totalKindBought,
        uint256 totalTradeVolumeUsd,
        uint256 totalQualifyingUsd,
        uint256 totalRewardUsd,
        uint256 totalClaimableRewardUsd,
        uint256 totalClaimedKind,
        uint256 rewardPoolKind,
        uint256 tier100Count,
        uint256 tier150Count,
        uint256 tier200Count,
        uint256 tier300Count,
        uint256 kindPriceUsd,
        uint256 startTime,
        uint256 endTime,
        uint256 claimEndTime,
        uint256 qualificationPeriod,
        uint256 claimCooldown,
        uint256 minPurchaseUsd,
        uint256 maxPurchaseUsd,
        uint256 claimCount,
        bool activated,
        bool closed
      ) v
    )`,

  "function getCompactKindPrice(uint256 campaignId) view returns (uint256)",


  /* ADMIN */

  "function setOperator(address operator,bool approved)",

  "function setApprovedKindPair(address pair,bool approved)",

  `function createCampaign(
      uint256 startTime,
      uint256 endTime,
      uint256 claimEndTime,
      uint256 qualificationPeriod,
      uint256 minPurchaseUsd,
      uint256 maxPurchaseUsd,
      uint256 claimCount,
      address usdtToken,
      address wbnbToken,
      address kindWbnbPair,
      address wbnbUsdtPair
    ) returns (uint256 campaignId)`,

  "function activateCampaign(uint256 campaignId)",

  "function fundRewardContract(uint256 campaignId,uint256 kindAmount)",

  "function disqualifyWallet(uint256 campaignId,address wallet,uint8 reason)",

  "function emergencyDisqualify(uint256 campaignId,address wallet)",

  "function closeCampaign(uint256 campaignId)",

  "function pause()",

  "function unpause()",

  "function withdrawExcessKind(uint256 campaignId,address receiver,uint256 kindAmount)",


  /* CUSTOM ERRORS */

  "error InvalidAddress()",
  "error InvalidAmount()",
  "error InvalidTime()",
  "error CampaignNotFound()",
  "error NotOperator()",
  "error NotActive()",
  "error CampaignClosedError()",
  "error AlreadyClosed()",
  "error BuyingEnded()",
  "error ClaimsExpired()",
  "error PairNotApproved()",
  "error TradeAlreadyRecorded()",
  "error InvalidTrade()",
  "error InvalidTradeTime()",
  "error NotQualifying()",
  "error QualificationEnded()",
  "error NotParticipant()",
  "error Disqualified()",
  "error AlreadyCompleted()",
  "error ClaimsComplete()",
  "error QualificationNotFinished()",
  "error MinimumNotMet()",
  "error ClaimCooldownActive()",
  "error InvalidReward()",
  "error InvalidPrice()",
  "error InsufficientRewardPool()",
  "error InvalidReason()",
  "error AlreadyDisqualified()",
  "error InvalidCampaignConfig()",
];


/* =========================================================
   TOKEN ABI
========================================================= */

const TOKEN_ABI = [
  "function approve(address spender,uint256 amount) returns (bool)",

  "function allowance(address owner,address spender) view returns (uint256)",

  "function balanceOf(address account) view returns (uint256)",

  "function decimals() view returns (uint8)",

  "function symbol() view returns (string)",
];


/* =========================================================
   CONSTANTS
========================================================= */

const ZERO = 0n;

const QUALIFICATION_SECONDS =
  48 * 60 * 60;

const CLAIM_COOLDOWN_SECONDS =
  24 * 60 * 60;

const MAX_PURCHASE_USD =
  "200";

const MAX_CLAIMS =
  4;


/* =========================================================
   HELPERS
========================================================= */

function normalizeChainId(value) {
  if (
    typeof value === "string" &&
    value.includes(":")
  ) {
    const pieces =
      value.split(":");

    return Number(
      pieces[
        pieces.length - 1
      ]
    );
  }

  return Number(
    value
  );
}


function shortAddress(value) {
  if (!value) {
    return "—";
  }

  return `${value.slice(
    0,
    6
  )}...${value.slice(-4)}`;
}


function usd(
  value,
  digits = 2
) {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        minimumFractionDigits:
          digits,

        maximumFractionDigits:
          digits,
      }
    );
  }
  catch {
    return "0.00";
  }
}


function token(
  value,
  decimals = 18,
  digits = 2
) {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        decimals
      )
    ).toLocaleString(
      undefined,
      {
        maximumFractionDigits:
          digits,
      }
    );
  }
  catch {
    return "0";
  }
}


function toUnix(value) {
  if (!value) {
    return 0;
  }

  const ms =
    new Date(
      value
    ).getTime();

  if (
    !Number.isFinite(ms)
  ) {
    return 0;
  }

  return Math.floor(
    ms / 1000
  );
}


function dateText(value) {
  const timestamp =
    Number(
      value || 0
    );

  if (!timestamp) {
    return "—";
  }

  return new Date(
    timestamp * 1000
  ).toLocaleString();
}


function durationText(
  value
) {
  const seconds =
    Number(
      value || 0
    );

  if (!seconds) {
    return "0";
  }

  const hours =
    Math.floor(
      seconds / 3600
    );

  if (
    hours % 24 === 0
  ) {
    return `${hours / 24} days`;
  }

  return `${hours} hours`;
}


function getErrorMessage(
  error
) {
  console.error(
    error
  );

  const name =
    error?.revert?.name ||
    error?.info?.error?.data?.name;

  if (name) {
    const niceErrors = {
      InvalidAddress:
        "Invalid address.",

      InvalidAmount:
        "Invalid amount.",

      InvalidTime:
        "Invalid campaign times.",

      CampaignNotFound:
        "Campaign does not exist.",

      NotOperator:
        "Wallet is not an authorized operator.",

      NotActive:
        "Campaign is not active or is already activated.",

      CampaignClosedError:
        "Campaign is closed.",

      AlreadyClosed:
        "Campaign is already closed.",

      BuyingEnded:
        "Campaign buying period has ended.",

      InvalidCampaignConfig:
        "Invalid campaign configuration.",

      InvalidPrice:
        "Unable to calculate KIND price.",

      InsufficientRewardPool:
        "Reward pool has insufficient KIND.",

      InvalidReason:
        "Invalid disqualification reason.",

      NotParticipant:
        "Wallet is not a participant.",

      AlreadyDisqualified:
        "Wallet is already disqualified.",

      AlreadyCompleted:
        "Wallet already completed the campaign.",
    };

    return (
      niceErrors[name] ||
      name
    );
  }

  return (
    error?.shortMessage ||
    error?.reason ||
    error?.info?.error?.message ||
    error?.message ||
    "Transaction failed."
  );
}


/* =========================================================
   ADMIN
========================================================= */

export default function Admin() {
  /* =======================================================
     APPKIT
  ======================================================= */

  const {
    address,
    isConnected,
  } =
    useAppKitAccount();

  const {
    walletProvider,
  } =
    useAppKitProvider(
      "eip155"
    );

  const {
    chainId,
  } =
    useAppKitNetwork();


  /* =======================================================
     READ PROVIDER
  ======================================================= */

  const readProvider =
    useMemo(
      () =>
        new JsonRpcProvider(
          BSC_RPC
        ),
      []
    );


  const readContract =
    useMemo(
      () =>
        new Contract(
          CONTRACT_ADDRESS,
          ABI,
          readProvider
        ),
      [
        readProvider,
      ]
    );


  /* =======================================================
     GENERAL STATE
  ======================================================= */

  const [
    owner,
    setOwner,
  ] =
    useState("");

  const [
    contractPaused,
    setContractPaused,
  ] =
    useState(false);

  const [
    activeCampaignId,
    setActiveCampaignId,
  ] =
    useState(ZERO);

  const [
    selectedCampaignId,
    setSelectedCampaignId,
  ] =
    useState(ZERO);

  const [
    globalData,
    setGlobalData,
  ] =
    useState(null);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    txLoading,
    setTxLoading,
  ] =
    useState(false);

  const [
    message,
    setMessage,
  ] =
    useState("");

  const [
    messageType,
    setMessageType,
  ] =
    useState("info");


  /* =======================================================
     MANUAL CAMPAIGN
  ======================================================= */

  const [
    manualCampaignId,
    setManualCampaignId,
  ] =
    useState("");


  /* =======================================================
     CREATE CAMPAIGN
  ======================================================= */

  const [
    startTime,
    setStartTime,
  ] =
    useState("");

  const [
    endTime,
    setEndTime,
  ] =
    useState("");

  const [
    claimEndTime,
    setClaimEndTime,
  ] =
    useState("");

  const [
    minPurchaseUsd,
    setMinPurchaseUsd,
  ] =
    useState("1");

  const [
    usdtToken,
    setUsdtToken,
  ] =
    useState(
      "0x55d398326f99059fF775485246999027B3197955"
    );

  const [
    wbnbToken,
    setWbnbToken,
  ] =
    useState(
      "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c"
    );

  const [
    kindWbnbPair,
    setKindWbnbPair,
  ] =
    useState(
      "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E"
    );

  const [
    wbnbUsdtPair,
    setWbnbUsdtPair,
  ] =
    useState(
      "0x16b9a82891338f9bA80E2D6970FddA79D1eb0daE"
    );


  /* =======================================================
     OPERATOR
  ======================================================= */

  const [
    operatorAddress,
    setOperatorAddress,
  ] =
    useState("");

  const [
    operatorAllowed,
    setOperatorAllowed,
  ] =
    useState(true);


  /* =======================================================
     APPROVED PAIR
  ======================================================= */

  const [
    pairAddress,
    setPairAddress,
  ] =
    useState("");

  const [
    pairAllowed,
    setPairAllowed,
  ] =
    useState(true);


  /* =======================================================
     REWARD FUNDING
  ======================================================= */

  const [
    fundAmount,
    setFundAmount,
  ] =
    useState("");

  const [
    kindDecimals,
    setKindDecimals,
  ] =
    useState(18);

  const [
    walletKindBalance,
    setWalletKindBalance,
  ] =
    useState(ZERO);

  const [
    contractKindBalance,
    setContractKindBalance,
  ] =
    useState(ZERO);


  /* =======================================================
     DISQUALIFY
  ======================================================= */

  const [
    dqWallet,
    setDqWallet,
  ] =
    useState("");

  const [
    dqReason,
    setDqReason,
  ] =
    useState("1");


  /* =======================================================
     WITHDRAW
  ======================================================= */

  const [
    withdrawReceiver,
    setWithdrawReceiver,
  ] =
    useState("");

  const [
    withdrawAmount,
    setWithdrawAmount,
  ] =
    useState("");


  /* =======================================================
     OWNER CHECK
  ======================================================= */

  const isOwner =
    useMemo(
      () => {
        if (
          !owner ||
          !address
        ) {
          return false;
        }

        return (
          owner.toLowerCase() ===
          address.toLowerCase()
        );
      },
      [
        owner,
        address,
      ]
    );


  const walletChainId =
    useMemo(
      () =>
        normalizeChainId(
          chainId
        ),
      [
        chainId,
      ]
    );


  /* =======================================================
     GET WRITE CONTRACT
  ======================================================= */

  const getWriteContract =
    useCallback(
      async () => {
        if (
          !walletProvider
        ) {
          throw new Error(
            "Connect wallet first."
          );
        }

        if (
          walletChainId !==
          BSC_CHAIN_ID
        ) {
          throw new Error(
            "Please switch to BSC Mainnet."
          );
        }

        const provider =
          new BrowserProvider(
            walletProvider
          );

        const signer =
          await provider.getSigner();

        return new Contract(
          CONTRACT_ADDRESS,
          ABI,
          signer
        );
      },
      [
        walletProvider,
        walletChainId,
      ]
    );


  /* =======================================================
     LOAD CAMPAIGN
  ======================================================= */

  const loadCampaign =
    useCallback(
      async (
        campaignId
      ) => {
        if (
          !campaignId ||
          campaignId === ZERO
        ) {
          setGlobalData(
            null
          );

          return;
        }

        const data =
          await readContract.getGlobalView(
            campaignId
          );

        setGlobalData(
          data
        );

        setSelectedCampaignId(
          campaignId
        );
      },
      [
        readContract,
      ]
    );


  /* =======================================================
     LOAD ADMIN DATA
  ======================================================= */

  const loadData =
    useCallback(
      async (
        showLoader = false
      ) => {
        try {
          if (
            showLoader
          ) {
            setLoading(
              true
            );
          }

          const [
            ownerAddress,
            pausedValue,
            activeId,
          ] =
            await Promise.all([
              readContract.owner(),

              readContract.paused(),

              readContract.getActiveCampaignId(),
            ]);


          setOwner(
            ownerAddress
          );

          setContractPaused(
            Boolean(
              pausedValue
            )
          );

          setActiveCampaignId(
            activeId
          );


          /*
           * Keep manually selected campaign if there is one.
           *
           * Otherwise select the current active campaign.
           */
          if (
            selectedCampaignId >
            ZERO
          ) {
            try {
              await loadCampaign(
                selectedCampaignId
              );
            }
            catch {
              setGlobalData(
                null
              );
            }
          }
          else if (
            activeId > ZERO
          ) {
            await loadCampaign(
              activeId
            );
          }
          else {
            setGlobalData(
              null
            );
          }
        }
        catch (error) {
          setMessageType(
            "error"
          );

          setMessage(
            getErrorMessage(
              error
            )
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        readContract,
        loadCampaign,
        selectedCampaignId,
      ]
    );


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(
    () => {
      loadData(
        true
      );

      const timer =
        setInterval(
          () => {
            loadData(
              false
            );
          },
          12000
        );

      return () =>
        clearInterval(
          timer
        );
    },
    [
      loadData,
    ]
  );


  /* =======================================================
     KIND BALANCES
  ======================================================= */

  const loadKindBalances =
    useCallback(
      async () => {
        try {
          const kind =
            new Contract(
              KIND_TOKEN_ADDRESS,
              TOKEN_ABI,
              readProvider
            );

          let decimals = 18;

          try {
            decimals =
              Number(
                await kind.decimals()
              );
          }
          catch {
            decimals = 18;
          }

          setKindDecimals(
            decimals
          );


          const contractBalance =
            await kind.balanceOf(
              CONTRACT_ADDRESS
            );

          setContractKindBalance(
            contractBalance
          );


          if (address) {
            const walletBalance =
              await kind.balanceOf(
                address
              );

            setWalletKindBalance(
              walletBalance
            );
          }
          else {
            setWalletKindBalance(
              ZERO
            );
          }
        }
        catch (error) {
          console.error(
            "KIND balance error:",
            error
          );
        }
      },
      [
        readProvider,
        address,
      ]
    );


  useEffect(
    () => {
      loadKindBalances();

      const timer =
        setInterval(
          loadKindBalances,
          12000
        );

      return () =>
        clearInterval(
          timer
        );
    },
    [
      loadKindBalances,
    ]
  );


  /* =======================================================
     TRANSACTION WRAPPER
  ======================================================= */

  const runTx =
    useCallback(
      async ({
        action,
        pending,
        success,
      }) => {
        try {
          setTxLoading(
            true
          );

          setMessageType(
            "info"
          );

          setMessage(
            pending
          );


          const tx =
            await action();


          setMessage(
            `${pending} Waiting for confirmation...`
          );


          await tx.wait();


          setMessageType(
            "success"
          );

          setMessage(
            success
          );


          /*
           * After campaign creation, clear manual selection
           * so newest active campaign loads automatically.
           */
          await loadData(
            false
          );

          await loadKindBalances();
        }
        catch (error) {
          setMessageType(
            "error"
          );

          setMessage(
            getErrorMessage(
              error
            )
          );
        }
        finally {
          setTxLoading(
            false
          );
        }
      },
      [
        loadData,
        loadKindBalances,
      ]
    );


  /* =======================================================
     CREATE CAMPAIGN
  ======================================================= */

  const handleCreateCampaign =
    useCallback(
      async () => {
        const start =
          toUnix(
            startTime
          );

        const end =
          toUnix(
            endTime
          );

        const claimEnd =
          toUnix(
            claimEndTime
          );


        if (
          !start ||
          !end ||
          !claimEnd
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter campaign start, campaign end and claim end."
          );

          return;
        }


        /*
         * Exact Solidity validation:
         *
         * startTime < endTime
         * endTime <= claimEndTime
         */
        if (
          start >= end
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Campaign end must be after campaign start."
          );

          return;
        }


        if (
          end > claimEnd
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Claim end cannot be before campaign end."
          );

          return;
        }


        const minValue =
          Number(
            minPurchaseUsd
          );


        if (
          !Number.isFinite(
            minValue
          ) ||
          minValue <= 0 ||
          minValue > 200
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Minimum purchase must be greater than $0 and not above $200."
          );

          return;
        }


        if (
          !usdtToken ||
          !wbnbToken ||
          !kindWbnbPair ||
          !wbnbUsdtPair
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter USDT, WBNB and both pair addresses."
          );

          return;
        }


        try {
          const contract =
            await getWriteContract();


          const minimum =
            parseUnits(
              minPurchaseUsd,
              18
            );


          const maximum =
            parseUnits(
              MAX_PURCHASE_USD,
              18
            );


          /*
           * IMPORTANT:
           *
           * Recent contract createCampaign() has exactly
           * 11 parameters.
           *
           * KIND token is NOT passed here.
           */
          await runTx({
            action: () =>
              contract.createCampaign(
                start,
                end,
                claimEnd,

                // exactly 48 hours
                QUALIFICATION_SECONDS,

                minimum,

                // exactly $200
                maximum,

                // exactly 4 claims
                MAX_CLAIMS,

                usdtToken,
                wbnbToken,
                kindWbnbPair,
                wbnbUsdtPair
              ),

            pending:
              "Creating campaign...",

            success:
              "Campaign created and activated successfully.",
          });


          /*
           * New campaign is automatically active,
           * so allow refresh to find it.
           */
          setSelectedCampaignId(
            ZERO
          );

          setManualCampaignId(
            ""
          );
        }
        catch (error) {
          setMessageType(
            "error"
          );

          setMessage(
            getErrorMessage(
              error
            )
          );
        }
      },
      [
        startTime,
        endTime,
        claimEndTime,
        minPurchaseUsd,
        usdtToken,
        wbnbToken,
        kindWbnbPair,
        wbnbUsdtPair,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     MANUAL CAMPAIGN
  ======================================================= */

  const handleLoadCampaign =
    useCallback(
      async () => {
        if (
          !manualCampaignId
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter campaign ID."
          );

          return;
        }


        const value =
          Number(
            manualCampaignId
          );


        if (
          !Number.isInteger(
            value
          ) ||
          value <= 0
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Invalid campaign ID."
          );

          return;
        }


        try {
          setLoading(
            true
          );


          const data =
            await readContract.getGlobalView(
              manualCampaignId
            );


          setSelectedCampaignId(
            data.campaignId
          );

          setGlobalData(
            data
          );


          setMessageType(
            "success"
          );

          setMessage(
            `Campaign #${manualCampaignId} loaded.`
          );
        }
        catch (error) {
          setMessageType(
            "error"
          );

          setMessage(
            getErrorMessage(
              error
            )
          );
        }
        finally {
          setLoading(
            false
          );
        }
      },
      [
        manualCampaignId,
        readContract,
      ]
    );


  const handleLoadActiveCampaign =
    useCallback(
      async () => {
        if (
          activeCampaignId ===
          ZERO
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "There is currently no open active campaign."
          );

          return;
        }

        setSelectedCampaignId(
          ZERO
        );

        setManualCampaignId(
          ""
        );

        await loadCampaign(
          activeCampaignId
        );
      },
      [
        activeCampaignId,
        loadCampaign,
      ]
    );


  /* =======================================================
     OPERATOR
  ======================================================= */

  const handleSetOperator =
    useCallback(
      async () => {
        if (
          !operatorAddress
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter operator address."
          );

          return;
        }


        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contract.setOperator(
              operatorAddress,
              operatorAllowed
            ),

          pending:
            operatorAllowed
              ? "Adding operator..."
              : "Removing operator...",

          success:
            operatorAllowed
              ? "Operator authorized."
              : "Operator removed.",
        });
      },
      [
        operatorAddress,
        operatorAllowed,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     APPROVED PAIR
  ======================================================= */

  const handleSetPair =
    useCallback(
      async () => {
        if (
          !pairAddress
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter KIND pair address."
          );

          return;
        }


        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contract.setApprovedKindPair(
              pairAddress,
              pairAllowed
            ),

          pending:
            pairAllowed
              ? "Approving KIND pair..."
              : "Removing KIND pair...",

          success:
            pairAllowed
              ? "KIND pair approved."
              : "KIND pair removed.",
        });
      },
      [
        pairAddress,
        pairAllowed,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     FUND REWARD POOL
  ======================================================= */

  const handleFund =
    useCallback(
      async () => {
        if (
          selectedCampaignId === ZERO &&
          activeCampaignId === ZERO
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Select a campaign first."
          );

          return;
        }


        if (
          !fundAmount ||
          Number(
            fundAmount
          ) <= 0
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter KIND amount."
          );

          return;
        }


        try {
          setTxLoading(
            true
          );


          if (
            !walletProvider
          ) {
            throw new Error(
              "Connect wallet first."
            );
          }


          if (
            walletChainId !==
            BSC_CHAIN_ID
          ) {
            throw new Error(
              "Please switch to BSC Mainnet."
            );
          }


          const provider =
            new BrowserProvider(
              walletProvider
            );


          const signer =
            await provider.getSigner();


          const kind =
            new Contract(
              KIND_TOKEN_ADDRESS,
              TOKEN_ABI,
              signer
            );


          let decimals =
            kindDecimals;


          try {
            decimals =
              Number(
                await kind.decimals()
              );
          }
          catch {
            decimals =
              kindDecimals;
          }


          const amount =
            parseUnits(
              fundAmount,
              decimals
            );


          const allowance =
            await kind.allowance(
              address,
              CONTRACT_ADDRESS
            );


          if (
            allowance < amount
          ) {
            setMessageType(
              "info"
            );

            setMessage(
              "Approving KIND..."
            );


            const approveTx =
              await kind.approve(
                CONTRACT_ADDRESS,
                amount
              );


            await approveTx.wait();
          }


          const contract =
            await getWriteContract();


          const cid =
            selectedCampaignId >
            ZERO
              ? selectedCampaignId
              : activeCampaignId;


          setMessage(
            "Funding reward contract..."
          );


          const tx =
            await contract.fundRewardContract(
              cid,
              amount
            );


          await tx.wait();


          setMessageType(
            "success"
          );

          setMessage(
            "Reward pool funded successfully."
          );


          await loadKindBalances();

          await loadCampaign(
            cid
          );
        }
        catch (error) {
          setMessageType(
            "error"
          );

          setMessage(
            getErrorMessage(
              error
            )
          );
        }
        finally {
          setTxLoading(
            false
          );
        }
      },
      [
        selectedCampaignId,
        activeCampaignId,
        fundAmount,
        walletProvider,
        walletChainId,
        kindDecimals,
        address,
        getWriteContract,
        loadKindBalances,
        loadCampaign,
      ]
    );


  /* =======================================================
     DISQUALIFY
  ======================================================= */

  const handleDisqualify =
    useCallback(
      async () => {
        const cid =
          selectedCampaignId >
          ZERO
            ? selectedCampaignId
            : activeCampaignId;


        if (
          cid === ZERO
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Select a campaign."
          );

          return;
        }


        if (
          !dqWallet
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter wallet address."
          );

          return;
        }


        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contract.disqualifyWallet(
              cid,
              dqWallet,
              Number(
                dqReason
              )
            ),

          pending:
            "Disqualifying wallet...",

          success:
            "Wallet disqualified.",
        });
      },
      [
        selectedCampaignId,
        activeCampaignId,
        dqWallet,
        dqReason,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     EMERGENCY DISQUALIFY
  ======================================================= */

  const handleEmergencyDisqualify =
    useCallback(
      async () => {
        const cid =
          selectedCampaignId >
          ZERO
            ? selectedCampaignId
            : activeCampaignId;


        if (
          cid === ZERO ||
          !dqWallet
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Select campaign and enter wallet."
          );

          return;
        }


        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contract.emergencyDisqualify(
              cid,
              dqWallet
            ),

          pending:
            "Emergency disqualification...",

          success:
            "Wallet emergency-disqualified.",
        });
      },
      [
        selectedCampaignId,
        activeCampaignId,
        dqWallet,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     PAUSE
  ======================================================= */

  const handlePause =
    useCallback(
      async () => {
        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contractPaused
              ? contract.unpause()
              : contract.pause(),

          pending:
            contractPaused
              ? "Unpausing contract..."
              : "Pausing contract...",

          success:
            contractPaused
              ? "Contract unpaused."
              : "Contract paused.",
        });
      },
      [
        contractPaused,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     CLOSE CAMPAIGN
  ======================================================= */

  const handleCloseCampaign =
    useCallback(
      async () => {
        const cid =
          selectedCampaignId >
          ZERO
            ? selectedCampaignId
            : activeCampaignId;


        if (
          cid === ZERO
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Select campaign first."
          );

          return;
        }


        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contract.closeCampaign(
              cid
            ),

          pending:
            "Closing campaign...",

          success:
            "Campaign closed. New trades and claims are stopped.",
        });
      },
      [
        selectedCampaignId,
        activeCampaignId,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     WITHDRAW EXCESS KIND
  ======================================================= */

  const handleWithdraw =
    useCallback(
      async () => {
        const cid =
          selectedCampaignId >
          ZERO
            ? selectedCampaignId
            : activeCampaignId;


        if (
          cid === ZERO
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Select campaign first."
          );

          return;
        }


        if (
          !withdrawReceiver ||
          !withdrawAmount
        ) {
          setMessageType(
            "error"
          );

          setMessage(
            "Enter receiver and KIND amount."
          );

          return;
        }


        const amount =
          parseUnits(
            withdrawAmount,
            kindDecimals
          );


        const contract =
          await getWriteContract();


        await runTx({
          action: () =>
            contract.withdrawExcessKind(
              cid,
              withdrawReceiver,
              amount
            ),

          pending:
            "Withdrawing excess KIND...",

          success:
            "Excess KIND withdrawn.",
        });
      },
      [
        selectedCampaignId,
        activeCampaignId,
        withdrawReceiver,
        withdrawAmount,
        kindDecimals,
        getWriteContract,
        runTx,
      ]
    );


  /* =======================================================
     CURRENT CAMPAIGN ID
  ======================================================= */

  const campaignId =
    selectedCampaignId >
    ZERO
      ? selectedCampaignId
      : activeCampaignId;


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>
          Loading Wallet Rush Admin...
        </div>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* =================================================
            HEADER
        ================================================= */}

        <header style={styles.header}>
          <div>
            <div style={styles.eyebrow}>
              KIND CAT TOKEN
            </div>

            <h1 style={styles.title}>
              Wallet Rush Admin
            </h1>

            <div style={styles.subtitle}>
              {shortAddress(
                CONTRACT_ADDRESS
              )}
              {" · "}
              BSC Mainnet
            </div>
          </div>


          <div style={styles.headerActions}>
            <div
              style={{
                ...styles.liveBadge,

                ...(contractPaused
                  ? styles.pausedBadge
                  : {}),
              }}
            >
              <span
                style={{
                  ...styles.dot,

                  ...(contractPaused
                    ? styles.redDot
                    : {}),
                }}
              />

              {contractPaused
                ? "Paused"
                : "Running"}
            </div>


            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() =>
                loadData(
                  true
                )
              }
            >
              Refresh
            </button>
          </div>
        </header>


        {/* =================================================
            OWNER / CONNECTION
        ================================================= */}

        <div style={styles.statsGrid}>
          <StatCard
            label="Connected Wallet"
            value={
              isConnected
                ? shortAddress(
                    address
                  )
                : "Not Connected"
            }
          />

          <StatCard
            label="Contract Owner"
            value={shortAddress(
              owner
            )}
          />

          <StatCard
            label="Access"
            value={
              isOwner
                ? "OWNER"
                : "NOT OWNER"
            }
          />

          <StatCard
            label="Wallet Network"
            value={
              walletChainId ===
              BSC_CHAIN_ID
                ? "BSC Mainnet"
                : `Chain ${walletChainId || "—"}`
            }
          />

          <StatCard
            label="Active Campaign"
            value={
              activeCampaignId >
              ZERO
                ? `#${activeCampaignId.toString()}`
                : "None"
            }
          />

          <StatCard
            label="Selected Campaign"
            value={
              campaignId > ZERO
                ? `#${campaignId.toString()}`
                : "None"
            }
          />
        </div>


        {!isOwner &&
          isConnected && (
            <div style={styles.warning}>
              Connected wallet is not the contract owner.
              Owner-only actions such as creating/closing a
              campaign, pair management, pause and withdrawal
              will revert.
            </div>
          )}


        {/* =================================================
            CAMPAIGN SELECTOR
        ================================================= */}

        <Section
          eyebrow="CAMPAIGN"
          title="Campaign Selection"
        >
          <div style={styles.formGrid2}>
            <Field
              label="Campaign ID"
              value={
                manualCampaignId
              }
              onChange={
                setManualCampaignId
              }
              placeholder="1"
              type="number"
            />

            <div style={styles.buttonField}>
              <span style={styles.label}>
                ACTION
              </span>

              <div style={styles.actionGrid}>
                <button
                  type="button"
                  style={styles.primaryButton}
                  onClick={
                    handleLoadCampaign
                  }
                >
                  Load Campaign
                </button>

                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={
                    handleLoadActiveCampaign
                  }
                >
                  Load Active
                </button>
              </div>
            </div>
          </div>


          <div style={styles.note}>
            Use manual Campaign ID to inspect an old or closed
            campaign. Your recent contract does not expose a
            latest/total campaigns getter.
          </div>
        </Section>


        {/* =================================================
            GLOBAL OVERVIEW
        ================================================= */}

        {globalData ? (
          <Section
            eyebrow="OVERVIEW"
            title={`Campaign #${campaignId.toString()}`}
          >
            <div style={styles.statsGrid}>
              <StatCard
                label="Campaign Status"
                value={
                  globalData.closed
                    ? "CLOSED"
                    : globalData.activated
                    ? "ACTIVE"
                    : "INACTIVE"
                }
              />

              <StatCard
                label="Participants"
                value={Number(
                  globalData.totalParticipants
                ).toLocaleString()}
              />

              <StatCard
                label="Qualified"
                value={Number(
                  globalData.totalQualified
                ).toLocaleString()}
              />

              <StatCard
                label="Completed"
                value={Number(
                  globalData.totalCompleted
                ).toLocaleString()}
              />

              <StatCard
                label="Disqualified"
                value={Number(
                  globalData.totalDisqualified
                ).toLocaleString()}
              />

              <StatCard
                label="Verified Trades"
                value={Number(
                  globalData.totalVerifiedTrades
                ).toLocaleString()}
              />

              <StatCard
                label="Full Trade Volume"
                value={`$${usd(
                  globalData.totalTradeVolumeUsd
                )}`}
              />

              <StatCard
                label="Qualifying Volume"
                value={`$${usd(
                  globalData.totalQualifyingUsd
                )}`}
              />

              <StatCard
                label="Total KIND Bought"
                value={token(
                  globalData.totalKindBought
                )}
              />

              <StatCard
                label="Assigned Reward USD"
                value={`$${usd(
                  globalData.totalRewardUsd
                )}`}
              />

              <StatCard
                label="Claimable USD"
                value={`$${usd(
                  globalData.totalClaimableRewardUsd
                )}`}
              />

              <StatCard
                label="Claimed KIND"
                value={token(
                  globalData.totalClaimedKind
                )}
              />

              <StatCard
                label="Reward Pool"
                value={`${token(
                  globalData.rewardPoolKind,
                  kindDecimals
                )} KIND`}
              />

              <StatCard
                label="KIND Price"
                value={`$${usd(
                  globalData.kindPriceUsd,
                  8
                )}`}
              />

              <StatCard
                label="Minimum Purchase"
                value={`$${usd(
                  globalData.minPurchaseUsd
                )}`}
              />

              <StatCard
                label="Maximum Purchase"
                value={`$${usd(
                  globalData.maxPurchaseUsd
                )}`}
              />

              <StatCard
                label="Qualification"
                value={durationText(
                  globalData.qualificationPeriod
                )}
              />

              <StatCard
                label="Claim Cooldown"
                value={durationText(
                  globalData.claimCooldown
                )}
              />

              <StatCard
                label="Claim Count"
                value={Number(
                  globalData.claimCount
                )}
              />

              <StatCard
                label="Campaign Start"
                value={dateText(
                  globalData.startTime
                )}
              />

              <StatCard
                label="Campaign End"
                value={dateText(
                  globalData.endTime
                )}
              />

              <StatCard
                label="Claim End"
                value={dateText(
                  globalData.claimEndTime
                )}
              />
            </div>


            <div style={styles.tierGrid}>
              <TierCard
                title="100%"
                range="$1 – $50"
                count={
                  globalData.tier100Count
                }
              />

              <TierCard
                title="150%"
                range="$50.01 – $100"
                count={
                  globalData.tier150Count
                }
              />

              <TierCard
                title="200%"
                range="$100.01 – $150"
                count={
                  globalData.tier200Count
                }
              />

              <TierCard
                title="300%"
                range="$150.01 – $200"
                count={
                  globalData.tier300Count
                }
              />
            </div>
          </Section>
        ) : (
          <div style={styles.noCampaign}>
            <div style={styles.bigIcon}>
              ⚡
            </div>

            <h2 style={styles.noCampaignTitle}>
              No Active Campaign
            </h2>

            <p style={styles.noCampaignText}>
              Create a new campaign or enter an existing
              campaign ID above.
            </p>
          </div>
        )}


        {/* =================================================
            CREATE CAMPAIGN
        ================================================= */}

        <Section
          eyebrow="NEW CAMPAIGN"
          title="Create Campaign"
        >
          <div style={styles.formGrid}>
            <Field
              label="Start Time"
              type="datetime-local"
              value={startTime}
              onChange={
                setStartTime
              }
            />

            <Field
              label="Campaign End"
              type="datetime-local"
              value={endTime}
              onChange={
                setEndTime
              }
            />

            <Field
              label="Claim End"
              type="datetime-local"
              value={claimEndTime}
              onChange={
                setClaimEndTime
              }
            />

            <Field
              label="Minimum Purchase USD"
              type="number"
              value={
                minPurchaseUsd
              }
              onChange={
                setMinPurchaseUsd
              }
              placeholder="1"
            />

            <Field
              label="Maximum Purchase"
              value="$200"
              disabled
            />

            <Field
              label="Qualification"
              value="48 Hours"
              disabled
            />

            <Field
              label="Claims"
              value="4"
              disabled
            />

            <Field
              label="Claim Cooldown"
              value="24 Hours"
              disabled
            />

            <Field
              label="USDT Token"
              value={
                usdtToken
              }
              onChange={
                setUsdtToken
              }
            />

            <Field
              label="WBNB Token"
              value={
                wbnbToken
              }
              onChange={
                setWbnbToken
              }
            />

            <Field
              label="KIND / WBNB Pair"
              value={
                kindWbnbPair
              }
              onChange={
                setKindWbnbPair
              }
            />

            <Field
              label="WBNB / USDT Pair"
              value={
                wbnbUsdtPair
              }
              onChange={
                setWbnbUsdtPair
              }
            />
          </div>


          <div style={styles.note}>
            KIND token is not submitted to createCampaign().
            Your Solidity contract uses the constructor KIND
            token automatically. Campaign is also activated
            automatically when created.
          </div>


          <button
            type="button"
            disabled={
              txLoading ||
              !isOwner
            }
            style={{
              ...styles.primaryButton,

              ...(txLoading ||
              !isOwner
                ? styles.disabled
                : {}),
            }}
            onClick={
              handleCreateCampaign
            }
          >
            {txLoading
              ? "Processing..."
              : "Create & Activate Campaign"}
          </button>
        </Section>


        {/* =================================================
            CONTRACT CONTROLS
        ================================================= */}

        <Section
          eyebrow="CONTROL"
          title="Campaign & Contract Controls"
        >
          <div style={styles.actionGrid}>
            <button
              type="button"
              disabled={
                txLoading ||
                !isOwner
              }
              style={{
                ...(contractPaused
                  ? styles.successButton
                  : styles.warningButton),

                ...(txLoading ||
                !isOwner
                  ? styles.disabled
                  : {}),
              }}
              onClick={
                handlePause
              }
            >
              {contractPaused
                ? "Unpause Contract"
                : "Pause Contract"}
            </button>


            <button
              type="button"
              disabled={
                txLoading ||
                !isOwner ||
                campaignId ===
                  ZERO ||
                Boolean(
                  globalData?.closed
                )
              }
              style={{
                ...styles.dangerButton,

                ...(txLoading ||
                !isOwner ||
                campaignId ===
                  ZERO ||
                Boolean(
                  globalData?.closed
                )
                  ? styles.disabled
                  : {}),
              }}
              onClick={
                handleCloseCampaign
              }
            >
              Close Campaign
            </button>
          </div>


          <div style={styles.note}>
            The recent contract lets the owner close a campaign
            at any time. Once closed, verified buys and claims
            stop for that campaign.
          </div>
        </Section>


        {/* =================================================
            OPERATOR
        ================================================= */}

        <Section
          eyebrow="BACKEND"
          title="Operator Management"
        >
          <div style={styles.formGrid2}>
            <Field
              label="Operator Address"
              value={
                operatorAddress
              }
              onChange={
                setOperatorAddress
              }
              placeholder="0x..."
            />

            <SelectField
              label="Permission"
              value={
                operatorAllowed
                  ? "true"
                  : "false"
              }
              onChange={(value) =>
                setOperatorAllowed(
                  value ===
                    "true"
                )
              }
              options={[
                {
                  value: "true",
                  label:
                    "Authorize Operator",
                },

                {
                  value: "false",
                  label:
                    "Remove Operator",
                },
              ]}
            />
          </div>


          <button
            type="button"
            disabled={
              txLoading ||
              !isOwner
            }
            style={{
              ...styles.primaryButton,

              ...(txLoading ||
              !isOwner
                ? styles.disabled
                : {}),
            }}
            onClick={
              handleSetOperator
            }
          >
            Update Operator
          </button>
        </Section>


        {/* =================================================
            KIND PAIR
        ================================================= */}

        <Section
          eyebrow="DEX"
          title="Approved KIND Pair"
        >
          <div style={styles.formGrid2}>
            <Field
              label="Pair Address"
              value={
                pairAddress
              }
              onChange={
                setPairAddress
              }
              placeholder="0x..."
            />

            <SelectField
              label="Permission"
              value={
                pairAllowed
                  ? "true"
                  : "false"
              }
              onChange={(value) =>
                setPairAllowed(
                  value ===
                    "true"
                )
              }
              options={[
                {
                  value: "true",
                  label:
                    "Approve Pair",
                },

                {
                  value: "false",
                  label:
                    "Remove Pair",
                },
              ]}
            />
          </div>


          <button
            type="button"
            disabled={
              txLoading ||
              !isOwner
            }
            style={{
              ...styles.primaryButton,

              ...(txLoading ||
              !isOwner
                ? styles.disabled
                : {}),
            }}
            onClick={
              handleSetPair
            }
          >
            Update Approved Pair
          </button>
        </Section>


        {/* =================================================
            FUND REWARD
        ================================================= */}

        <Section
          eyebrow="REWARD POOL"
          title="Fund Reward Contract"
        >
          <div style={styles.statsGrid}>
            <StatCard
              label="KIND Token"
              value={shortAddress(
                KIND_TOKEN_ADDRESS
              )}
            />

            <StatCard
              label="Your KIND Balance"
              value={`${token(
                walletKindBalance,
                kindDecimals
              )} KIND`}
            />

            <StatCard
              label="Contract KIND Balance"
              value={`${token(
                contractKindBalance,
                kindDecimals
              )} KIND`}
            />

            <StatCard
              label="Selected Campaign"
              value={
                campaignId > ZERO
                  ? `#${campaignId.toString()}`
                  : "None"
              }
            />
          </div>


          <div
            style={{
              ...styles.formGrid2,
              marginTop:
                "15px",
            }}
          >
            <Field
              label="KIND Amount"
              type="number"
              value={
                fundAmount
              }
              onChange={
                setFundAmount
              }
              placeholder="100000"
            />

            <div style={styles.buttonField}>
              <span style={styles.label}>
                FUND
              </span>

              <button
                type="button"
                disabled={
                  txLoading ||
                  campaignId ===
                    ZERO
                }
                style={{
                  ...styles.primaryButton,

                  ...(txLoading ||
                  campaignId ===
                    ZERO
                    ? styles.disabled
                    : {}),
                }}
                onClick={
                  handleFund
                }
              >
                Fund Reward Pool
              </button>
            </div>
          </div>


          <div style={styles.note}>
            This checks KIND allowance first. If required, your
            wallet sends approve(), then the admin page calls
            fundRewardContract().
          </div>
        </Section>


        {/* =================================================
            DISQUALIFICATION
        ================================================= */}

        <Section
          eyebrow="SECURITY"
          title="Disqualify Participant"
        >
          <div style={styles.formGrid2}>
            <Field
              label="Participant Wallet"
              value={
                dqWallet
              }
              onChange={
                setDqWallet
              }
              placeholder="0x..."
            />

            <SelectField
              label="Reason"
              value={
                dqReason
              }
              onChange={
                setDqReason
              }
              options={[
                {
                  value: "1",
                  label:
                    "KIND Sold",
                },

                {
                  value: "2",
                  label:
                    "KIND Sent",
                },

                {
                  value: "3",
                  label:
                    "KIND Received",
                },

                {
                  value: "4",
                  label:
                    "Invalid Trade",
                },

                {
                  value: "5",
                  label:
                    "Unauthorized Contract",
                },

                {
                  value: "6",
                  label:
                    "Admin Emergency",
                },
              ]}
            />
          </div>


          <div style={styles.actionGrid}>
            <button
              type="button"
              disabled={
                txLoading ||
                campaignId ===
                  ZERO
              }
              style={{
                ...styles.dangerButton,

                ...(txLoading ||
                campaignId ===
                  ZERO
                  ? styles.disabled
                  : {}),
              }}
              onClick={
                handleDisqualify
              }
            >
              Disqualify
            </button>


            <button
              type="button"
              disabled={
                txLoading ||
                !isOwner ||
                campaignId ===
                  ZERO
              }
              style={{
                ...styles.dangerButton,

                ...(txLoading ||
                !isOwner ||
                campaignId ===
                  ZERO
                  ? styles.disabled
                  : {}),
              }}
              onClick={
                handleEmergencyDisqualify
              }
            >
              Emergency Disqualify
            </button>
          </div>
        </Section>


        {/* =================================================
            WITHDRAW
        ================================================= */}

        <Section
          eyebrow="TREASURY"
          title="Withdraw Excess KIND"
        >
          <div style={styles.formGrid2}>
            <Field
              label="Receiver Address"
              value={
                withdrawReceiver
              }
              onChange={
                setWithdrawReceiver
              }
              placeholder="0x..."
            />

            <Field
              label="KIND Amount"
              type="number"
              value={
                withdrawAmount
              }
              onChange={
                setWithdrawAmount
              }
              placeholder="1000"
            />
          </div>


          <button
            type="button"
            disabled={
              txLoading ||
              !isOwner ||
              campaignId ===
                ZERO ||
              !globalData?.closed
            }
            style={{
              ...styles.dangerButton,

              ...(txLoading ||
              !isOwner ||
              campaignId ===
                ZERO ||
              !globalData?.closed
                ? styles.disabled
                : {}),
            }}
            onClick={
              handleWithdraw
            }
          >
            Withdraw Excess KIND
          </button>


          <div style={styles.note}>
            Your Solidity contract only allows excess KIND
            withdrawal after the selected campaign has been
            closed.
          </div>
        </Section>


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (
          <div
            style={{
              ...styles.toast,

              ...(messageType ===
              "success"
                ? styles.toastSuccess
                : {}),

              ...(messageType ===
              "error"
                ? styles.toastError
                : {}),
            }}
          >
            <span>
              {message}
            </span>

            <button
              type="button"
              style={styles.toastClose}
              onClick={() =>
                setMessage("")
              }
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/* =========================================================
   COMPONENTS
========================================================= */

function Section({
  eyebrow,
  title,
  children,
}) {
  return (
    <section style={styles.section}>
      <div style={styles.sectionHeader}>
        <div style={styles.sectionEyebrow}>
          {eyebrow}
        </div>

        <h2 style={styles.sectionTitle}>
          {title}
        </h2>
      </div>

      {children}
    </section>
  );
}


function StatCard({
  label,
  value,
}) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>
        {label}
      </div>

      <div style={styles.statValue}>
        {value}
      </div>
    </div>
  );
}


function Field({
  label,
  value,
  onChange = () => {},
  type = "text",
  placeholder = "",
  disabled = false,
}) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>
        {label}
      </span>

      <input
        type={type}
        value={value}
        placeholder={
          placeholder
        }
        disabled={
          disabled
        }
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        style={{
          ...styles.input,

          ...(disabled
            ? styles.inputDisabled
            : {}),
        }}
      />
    </label>
  );
}


function SelectField({
  label,
  value,
  onChange,
  options,
}) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>
        {label}
      </span>

      <select
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        style={styles.input}
      >
        {options.map(
          (option) => (
            <option
              key={
                option.value
              }
              value={
                option.value
              }
            >
              {
                option.label
              }
            </option>
          )
        )}
      </select>
    </label>
  );
}


function TierCard({
  title,
  range,
  count,
}) {
  return (
    <div style={styles.tierCard}>
      <div style={styles.tierPercent}>
        {title}
      </div>

      <div style={styles.tierRange}>
        {range}
      </div>

      <div style={styles.tierUsers}>
        {Number(
          count || 0
        ).toLocaleString()}{" "}
        users
      </div>
    </div>
  );
}


/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight:
      "100vh",

    padding:
      "30px 18px 90px",

    color:
      "#ffffff",

    fontFamily:
      "Inter, Arial, sans-serif",

    background:
      "radial-gradient(circle at 10% 0%, rgba(112,76,255,.20), transparent 30%), radial-gradient(circle at 90% 10%, rgba(130,82,255,.08), transparent 30%), #07090d",
  },


  container: {
    width:
      "100%",

    maxWidth:
      "1450px",

    margin:
      "0 auto",
  },


  header: {
    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "space-between",

    gap:
      "20px",

    flexWrap:
      "wrap",

    marginBottom:
      "22px",
  },


  eyebrow: {
    color:
      "#8a72ff",

    fontSize:
      "10px",

    fontWeight:
      900,

    letterSpacing:
      "2px",
  },


  title: {
    margin:
      "7px 0 0",

    fontSize:
      "34px",

    fontWeight:
      900,

    letterSpacing:
      "-1px",
  },


  subtitle: {
    marginTop:
      "7px",

    color:
      "#717887",

    fontSize:
      "12px",
  },


  headerActions: {
    display:
      "flex",

    gap:
      "10px",

    alignItems:
      "center",
  },


  liveBadge: {
    display:
      "flex",

    alignItems:
      "center",

    gap:
      "7px",

    padding:
      "11px 14px",

    borderRadius:
      "12px",

    border:
      "1px solid rgba(89,225,143,.25)",

    background:
      "rgba(89,225,143,.07)",

    color:
      "#60e596",

    fontSize:
      "11px",

    fontWeight:
      850,
  },


  pausedBadge: {
    border:
      "1px solid rgba(255,97,119,.25)",

    background:
      "rgba(255,97,119,.07)",

    color:
      "#ff7186",
  },


  dot: {
    width:
      "7px",

    height:
      "7px",

    borderRadius:
      "50%",

    background:
      "#5ce690",
  },


  redDot: {
    background:
      "#ff6177",
  },


  statsGrid: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(180px,1fr))",

    gap:
      "11px",
  },


  statCard: {
    minWidth:
      0,

    padding:
      "17px",

    borderRadius:
      "15px",

    border:
      "1px solid #202630",

    background:
      "linear-gradient(145deg,#11151c,#0c0f15)",
  },


  statLabel: {
    marginBottom:
      "7px",

    color:
      "#686f7d",

    fontSize:
      "9px",

    fontWeight:
      850,

    letterSpacing:
      ".8px",

    textTransform:
      "uppercase",
  },


  statValue: {
    color:
      "#f1f2f5",

    fontSize:
      "15px",

    fontWeight:
      800,

    overflowWrap:
      "anywhere",
  },


  warning: {
    marginTop:
      "14px",

    padding:
      "13px 15px",

    borderRadius:
      "12px",

    border:
      "1px solid rgba(236,179,87,.27)",

    background:
      "rgba(236,179,87,.07)",

    color:
      "#e9b867",

    fontSize:
      "12px",

    lineHeight:
      1.6,
  },


  section: {
    marginTop:
      "18px",

    padding:
      "22px",

    borderRadius:
      "21px",

    border:
      "1px solid #202631",

    background:
      "linear-gradient(145deg,#101319,#0b0e13)",

    boxShadow:
      "0 18px 50px rgba(0,0,0,.14)",
  },


  sectionHeader: {
    marginBottom:
      "18px",
  },


  sectionEyebrow: {
    color:
      "#8069ed",

    fontSize:
      "9px",

    fontWeight:
      900,

    letterSpacing:
      "1.5px",
  },


  sectionTitle: {
    margin:
      "5px 0 0",

    fontSize:
      "21px",

    fontWeight:
      850,
  },


  formGrid: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(220px,1fr))",

    gap:
      "12px",

    marginBottom:
      "15px",
  },


  formGrid2: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(280px,1fr))",

    gap:
      "12px",

    marginBottom:
      "15px",
  },


  field: {
    display:
      "block",
  },


  label: {
    display:
      "block",

    marginBottom:
      "7px",

    color:
      "#717887",

    fontSize:
      "9px",

    fontWeight:
      850,

    letterSpacing:
      ".8px",

    textTransform:
      "uppercase",
  },


  input: {
    width:
      "100%",

    boxSizing:
      "border-box",

    padding:
      "12px 13px",

    borderRadius:
      "11px",

    border:
      "1px solid #282e3a",

    outline:
      "none",

    background:
      "#090c11",

    color:
      "#ffffff",

    fontSize:
      "13px",
  },


  inputDisabled: {
    opacity:
      0.55,

    cursor:
      "not-allowed",
  },


  buttonField: {
    display:
      "flex",

    flexDirection:
      "column",

    justifyContent:
      "flex-end",
  },


  actionGrid: {
    display:
      "flex",

    flexWrap:
      "wrap",

    alignItems:
      "center",

    gap:
      "10px",
  },


  primaryButton: {
    padding:
      "12px 18px",

    border:
      0,

    borderRadius:
      "11px",

    color:
      "#ffffff",

    background:
      "linear-gradient(135deg,#6f55f2,#9e68ff)",

    cursor:
      "pointer",

    fontSize:
      "12px",

    fontWeight:
      850,
  },


  secondaryButton: {
    padding:
      "11px 17px",

    border:
      "1px solid #343a47",

    borderRadius:
      "11px",

    color:
      "#bec2cc",

    background:
      "#151920",

    cursor:
      "pointer",

    fontSize:
      "12px",

    fontWeight:
      800,
  },


  warningButton: {
    padding:
      "12px 18px",

    border:
      0,

    borderRadius:
      "11px",

    background:
      "#e4b158",

    color:
      "#161005",

    cursor:
      "pointer",

    fontSize:
      "12px",

    fontWeight:
      900,
  },


  successButton: {
    padding:
      "12px 18px",

    border:
      0,

    borderRadius:
      "11px",

    background:
      "#61e497",

    color:
      "#07140d",

    cursor:
      "pointer",

    fontSize:
      "12px",

    fontWeight:
      900,
  },


  dangerButton: {
    padding:
      "12px 18px",

    border:
      0,

    borderRadius:
      "11px",

    color:
      "#ffffff",

    background:
      "linear-gradient(135deg,#b53d53,#df526c)",

    cursor:
      "pointer",

    fontSize:
      "12px",

    fontWeight:
      850,
  },


  disabled: {
    opacity:
      0.4,

    cursor:
      "not-allowed",
  },


  note: {
    marginTop:
      "13px",

    marginBottom:
      "14px",

    padding:
      "12px 14px",

    borderRadius:
      "11px",

    border:
      "1px solid #222832",

    background:
      "#0a0d12",

    color:
      "#747b89",

    fontSize:
      "11px",

    lineHeight:
      1.6,
  },


  tierGrid: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit,minmax(170px,1fr))",

    gap:
      "10px",

    marginTop:
      "15px",
  },


  tierCard: {
    padding:
      "17px",

    borderRadius:
      "14px",

    border:
      "1px solid #212732",

    background:
      "#0a0d12",
  },


  tierPercent: {
    fontSize:
      "25px",

    fontWeight:
      900,

    color:
      "#ffffff",
  },


  tierRange: {
    marginTop:
      "5px",

    color:
      "#777e8b",

    fontSize:
      "11px",
  },


  tierUsers: {
    marginTop:
      "12px",

    color:
      "#947dff",

    fontSize:
      "11px",

    fontWeight:
      850,
  },


  noCampaign: {
    marginTop:
      "18px",

    padding:
      "48px 20px",

    textAlign:
      "center",

    border:
      "1px solid #202631",

    borderRadius:
      "21px",

    background:
      "#0d1015",
  },


  bigIcon: {
    fontSize:
      "30px",
  },


  noCampaignTitle: {
    margin:
      "12px 0 5px",

    fontSize:
      "21px",
  },


  noCampaignText: {
    margin:
      0,

    color:
      "#717887",

    fontSize:
      "12px",
  },


  toast: {
    position:
      "fixed",

    right:
      "20px",

    bottom:
      "20px",

    zIndex:
      999,

    display:
      "flex",

    alignItems:
      "center",

    gap:
      "14px",

    maxWidth:
      "480px",

    padding:
      "14px 16px",

    borderRadius:
      "13px",

    border:
      "1px solid #363c48",

    background:
      "#171b23",

    color:
      "#d4d7de",

    fontSize:
      "12px",

    boxShadow:
      "0 18px 60px rgba(0,0,0,.55)",
  },


  toastSuccess: {
    border:
      "1px solid rgba(87,225,145,.38)",

    color:
      "#71eaa7",
  },


  toastError: {
    border:
      "1px solid rgba(255,92,115,.38)",

    color:
      "#ff7b8f",
  },


  toastClose: {
    marginLeft:
      "auto",

    padding:
      0,

    border:
      0,

    background:
      "transparent",

    color:
      "inherit",

    fontSize:
      "19px",

    cursor:
      "pointer",
  },


  loading: {
    maxWidth:
      "500px",

    margin:
      "120px auto",

    padding:
      "40px",

    borderRadius:
      "20px",

    border:
      "1px solid #222833",

    background:
      "#101319",

    textAlign:
      "center",

    color:
      "#858c99",
  },
};